import { Activity, Cable, CheckCircle2, Clock3, Cpu, Router, Server, ShieldAlert, Wifi, Zap } from 'lucide-solid';
import { createMemo, Show, type JSX } from 'solid-js';
import { EmptyState, InlineNotice, Metric, ScreenHeader } from '../components/ui';
import { isFreshHeartbeat, timeAgo } from '../lib/format';
import { hasAuthoredContinuousRuns, roomRigCounts, roomRigSummary } from '../lib/panelElements';
import { controllerNetworkName } from '../lib/wifi';
import type { ControllerHealthLive } from '../lib/controllerHealth';
import { useVenue } from '../state/VenueProvider';
import { TEST_IDS } from '../testIds';

function duration(seconds: number): string {
  if (!Number.isFinite(seconds)) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return [days ? `${days}d` : '', hours ? `${hours}h` : '', `${minutes}m`].filter(Boolean).join(' ');
}

function ControllerHealthFields(props: { live: ControllerHealthLive }): JSX.Element {
  return (
    <div class="status-health-fields">
      <Metric label="State" value={props.live.state} />
      <Metric label="Frames applied" value={props.live.framesApplied.toLocaleString()} />
      <Metric label="Worst lateness" value={props.live.worstFrameLatenessUs === undefined ? '—' : `${props.live.worstFrameLatenessUs.toLocaleString()} µs`} />
      <Metric label="Min heap" value={`${props.live.internalHeapMinBytes.toLocaleString()} bytes`} />
    </div>
  );
}

export function StatusScreen(): JSX.Element {
  const venue = useVenue();
  const status = createMemo(() => venue.lanStatus());
  const engine = createMemo(() => status()?.engine ?? {});
  const daemon = createMemo(() => status()?.daemon ?? {});
  const session = createMemo(() => status()?.session ?? {});
  const output = createMemo(() => status()?.outputBinding ?? status()?.output ?? {});
  const rigCounts = createMemo(() => roomRigCounts(
    hasAuthoredContinuousRuns(venue.roomLayout()) ? venue.roomModel() : undefined,
    venue.rig(),
  ));

  /**
   * What the room is made of, in the units an operator uses: runs and pixels.
   *
   * The universe count used to be appended here. "Universe" is our word for a block of DMX addressing,
   * it changes nothing anyone reading this screen can act on, and the copy law forbids it outright.
   */
  const roomSummary = (): string => {
    const binding = venue.selectedRoom()?.rigs?.find((entry) => entry.rigId === venue.selectedRigId());
    if (!venue.roomStatus()?.activeRig && binding?.binding?.state !== 'bound') return 'Not set up yet';
    const counts = rigCounts();
    return counts.pixels ? roomRigSummary(counts) : 'Not reported yet';
  };

  /**
   * Where DMX is going, in the one form an operator can act on: the address it is being sent to.
   *
   * The controller publishes its egress binding as `{interface, state, ip}`. The interface name (`eth0`)
   * is our word for a socket rather than the operator's, and `bound` is our word for a state they cannot
   * change, so neither is rendered — only the address, which the owner asked to keep visible (2026-09-15).
   * `bound` is the one state that resolves an address; `waiting` (the interface is up with no address yet,
   * output held) and `unbound` (the configured interface is absent) both read as not connected.
   */
  const outputAddress = createMemo(() => {
    const binding = output();
    return binding.state === 'bound' && binding.ip ? String(binding.ip) : 'Not connected';
  });

  const update = createMemo(() => status()?.update ?? venue.deviceDoc()?.update ?? venue.deviceDoc()?.updateState ?? {});
  const cloudOnline = createMemo(() => Boolean(venue.mirror()?.heartbeat?.online && isFreshHeartbeat(venue.mirror()?.heartbeat?.lastSeenAt)));
  const microSelected = createMemo(() => venue.boundDevice(venue.selectedDeviceId())?.deviceClass === 'sculpture');
  /**
   * The network the controller is on, from its own mirror — the one source that reaches a hosted console.
   * It is stated once per screen: in the Connection list when a direct link fills this page out, and in
   * the overview strip when the cloud is all there is.
   */
  const controllerNetwork = createMemo(() => controllerNetworkName(venue.mirror()?.network));
  const controllerStatusLabel = createMemo(() => ({
    online: 'Registered and online',
    provisioning: 'Waiting to be claimed to a room',
    revoked: 'Access revoked',
  })[String(venue.selectedController()?.status ?? '')] ?? 'Not reported yet');

  const updateLabel = createMemo(() => ({
    idle: 'No update in progress',
    downloading: 'Downloading an update',
    verifying: 'Verifying the update',
    installing: 'Installing the update',
    restarting: 'Restarting to finish the update',
    healthy: 'Update installed and healthy',
    'rolled-back': 'Update rolled back',
    failed: 'The last update did not finish',
  })[String(update().state ?? update().status ?? '')] ?? 'No update in progress');

  return (
    <>
      <ScreenHeader
        title="Status"
        description="Controller health."
      />

      <Show when={status()} fallback={
        <>
          <InlineNotice>Connect this device directly to the controller to see engine, output binding and runtime details.</InlineNotice>
          <section class="status-overview cloud-only-status surface" classList={{ 'without-active-source': Boolean(venue.controlOwnership()), 'with-network': Boolean(controllerNetwork()) }}>
            <Metric label="Controller" value={venue.selectedController()?.displayName || 'Not selected'}
              hint={microSelected() ? undefined : cloudOnline() ? 'Online now' : `Offline since ${timeAgo(venue.selectedController()?.lastSeenAt)}`}
              tone={microSelected() ? venue.selectedController()?.status === 'online' || venue.controllerHealthLive()?.state === 'active' ? 'good' : 'warn' : cloudOnline() ? 'good' : 'warn'} />
            <Metric label="Connection" value={controllerStatusLabel()} hint="What the room records for this controller" />
            <Show when={controllerNetwork()}>{(network) => <Metric label="Network" value={<span data-testid={TEST_IDS.controllerNetwork}>{network()}</span>} />}</Show>
            <Show when={!venue.controlOwnership()}><Metric label="Active source" value={venue.activeSourceLabel()} /></Show>
            <Metric label="Room" value={roomSummary()} />
            <Show when={venue.controllerHealthLive()}>{(live) => <ControllerHealthFields live={live()} />}</Show>
          </section>
        </>
      }>
        <section class="status-overview surface" classList={{ 'without-active-source': Boolean(venue.controlOwnership()) }}>
          <Metric label="Controller software" value={status()?.version || 'development'} tone="good" />
          <Metric label="Uptime" value={duration(Number(daemon().uptimeSeconds ?? 0))} hint={`Started ${timeAgo(String(daemon().startedAt ?? ''))}`} />
          <Show when={!venue.controlOwnership()}><Metric label="Active source" value={venue.activeSourceLabel()} hint={venue.controllerState()?.blackout ? 'Blackout is on' : 'Output is live'} tone={venue.controllerState()?.blackout ? 'warn' : 'good'} /></Show>
          <Metric
            label="Output rate"
            value={Number(engine().actualRateHz ?? venue.controllerState()?.outputRateHz) > 0 ? `${Number(engine().actualRateHz ?? venue.controllerState()?.outputRateHz).toFixed(1)} Hz` : '—'}
            hint={Number(engine().targetRateHz ?? venue.controllerState()?.outputRateHz) > 0 ? `${Number(engine().targetRateHz ?? venue.controllerState()?.outputRateHz)} Hz target` : undefined}
          />
          <Show when={venue.controllerHealthLive()}>{(live) => <ControllerHealthFields live={live()} />}</Show>
        </section>

        <div class="status-columns">
          <section class="surface health-section">
            <div class="section-heading"><div><h2>Output engine</h2></div><Zap size={20} /></div>
            <div class="health-list">
              <div><span><Activity size={17} /></span><div><small>Engine</small><strong>{engine().running === false ? 'Stopped' : 'Running'}</strong></div><i class={engine().running === false ? 'bad' : 'good'} /></div>
              <div><span><Cable size={17} /></span><div><small>DMX output</small><strong>{outputAddress()}</strong></div></div>
              <div><span><Zap size={17} /></span><div><small>Intensity ceiling</small><strong>{Math.round(Number(engine().maxIntensity ?? status()?.maxIntensity ?? 1) * 100)}%</strong></div></div>
            </div>
          </section>

          <section class="surface health-section">
            <div class="section-heading"><div><h2>Connection</h2></div><Wifi size={20} /></div>
            <div class="health-list">
              <div><span><Wifi size={17} /></span><div><small>Cloud session</small><strong>{session().state === 'active' ? 'Connected' : session().state ? 'Not connected' : 'Not reported yet'}</strong></div><i class={session().state === 'active' ? 'good' : 'warn'} /></div>
              <Show when={controllerNetwork()}>{(network) => (
                <div><span><Router size={17} /></span><div><small>Network</small><strong data-testid={TEST_IDS.controllerNetwork}>{network()}</strong></div></div>
              )}</Show>
              <Show when={venue.staff()}>
                <div><span><Cpu size={17} /></span><div><small>Runtime</small><strong>{Number(status()?.runtime?.goroutines ?? 0)} routines</strong></div><em>{Math.round(Number(status()?.runtime?.allocBytes ?? 0) / 1_048_576)} MB active</em></div>
              </Show>
            </div>
          </section>
        </div>

        <Show when={update().state || update().status || update().version}>
          <section class="surface update-section">
            <div class="section-heading"><div><h2>Software update</h2><p>Controller release posture</p></div><CheckCircle2 size={20} /></div>
            <div class="update-strip">
              <span class="update-icon"><ShieldAlert size={21} /></span>
              <div><strong>{updateLabel()}</strong><p>Version {String(update().version ?? update().targetVersion ?? status()?.version ?? '—')} · {String(update().channel ?? venue.deviceDoc()?.updateChannel ?? 'stable')} channel</p></div>
              <span class="update-time"><Clock3 size={15} /> {timeAgo(update().updatedAt as string | undefined)}</span>
            </div>
          </section>
        </Show>

      </Show>

      <Show when={!venue.selectedController()}><EmptyState icon={<Server size={24} />} title="Choose a controller" detail="Select a controller on Connect to see its health." /></Show>
    </>
  );
}
